import { useMemo, useState } from "react";
import { useRouteLoaderData, useNavigate } from "react-router";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Scale, User } from "lucide-react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";

const AllJudges = () => {
	const navigate = useNavigate();
	const user = useAuthenticatedUser();
	const { judges, projects, codes } = useRouteLoaderData(
		WORKSPACE_LOADER_ID,
	) as WorkspaceLoaderData;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [showOnlyOwn, setShowOnlyOwn] = useState(false);
	const {
		createProject,
		createCode,
		createJudge,
		updateProjectTitle,
		updateCodeTitle,
		updateJudgeTitle,
	} = useWorkspaceSidebarActions();

	const filteredJudges = useMemo(() => {
		if (!showOnlyOwn || !user?.id) return judges;
		return judges.filter((judge) => judge.user_id === user.id);
	}, [judges, showOnlyOwn, user?.id]);

	const handleJudgeClick = (judgeId: string) => {
		navigate(`/workspace/judges/${judgeId}`);
	};

	return (
		<SidebarProvider>
			<div className="min-h-screen flex w-full">
				<WorkspaceSidebar
					projects={projects}
					codes={codes}
					judges={filteredJudges}
					onNewProject={createProject}
					onNewCode={createCode}
					onNewJudge={createJudge}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onUpdateJudgeTitle={updateJudgeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1 overflow-auto">
					<div className="container mx-auto py-8 px-4">
						<div className="mb-6 flex items-start justify-between">
							<div>
								<h1 className="text-4xl font-bold text-foreground">
									All Judges
								</h1>
								<p className="text-muted-foreground mt-2">
									Browse and select from all available judges
								</p>
							</div>
							<div className="flex items-center gap-4">
								<div className="flex items-center space-x-2">
									<Switch
										id="show-only-own"
										checked={showOnlyOwn}
										onCheckedChange={(checked) =>
											setShowOnlyOwn(Boolean(checked))
										}
									/>
									<Label
										htmlFor="show-only-own"
										className="text-sm font-medium cursor-pointer"
									>
										Only my judges
									</Label>
								</div>
								<Button onClick={() => createJudge()} size="lg">
									<Scale className="mr-2 h-4 w-4" />
									Create New
								</Button>
							</div>
						</div>

						{filteredJudges.length === 0 ? (
							<Card>
								<CardContent className="py-12 text-center">
									<Scale className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
									<p className="text-muted-foreground">No judges found</p>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{filteredJudges.map((judge) => (
									<Card
										key={judge.id}
										className="cursor-pointer hover:border-primary transition-colors"
										data-testid={`judge-card-${judge.id}`}
										onClick={() => handleJudgeClick(judge.id)}
									>
										<CardHeader>
											<div className="flex items-start justify-between">
												<div className="flex items-center gap-2">
													<Scale className="h-5 w-5 text-primary" />
													<CardTitle className="text-lg">
														{judge.title}
													</CardTitle>
												</div>
												{judge.is_public && (
													<Badge variant="secondary">Public</Badge>
												)}
											</div>
											<CardDescription className="flex items-center gap-1 mt-2">
												<User className="h-3 w-3" />
												<span className="text-xs">
													{judge.user_id === user?.id
														? "You"
														: judge.owner_email}
												</span>
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-2">
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Model:
													</span>
													<p className="text-sm">{judge.model}</p>
												</div>
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														System Prompt:
													</span>
													<p className="text-sm line-clamp-2">
														{judge.system_prompt ?? "No prompt set"}
													</p>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</div>
				</main>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
			</div>
		</SidebarProvider>
	);
};

export default AllJudges;
